import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Booking, BookingSettings, Availability } from '@/types/booking';
import { emailService } from './email';

const BOOKINGS_COLLECTION = 'bookings';
const SETTINGS_COLLECTION = 'bookingSettings';

const firestoreDb: typeof db = db;

type CalendarSyncAction = 'create' | 'update' | 'cancel';

const toCalendarPayload = (booking: Booking) => ({
  meetingType: booking.meetingType,
  attendeeName: booking.attendeeName,
  attendeeEmail: booking.attendeeEmail,
  attendeePhone: booking.attendeePhone,
  companyName: booking.companyName,
  message: booking.message,
  scheduledDate: (() => {
    const d = booking.scheduledDate;
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  })(),
  scheduledTime: booking.scheduledTime,
  timezone: booking.timezone,
  status: booking.status,
  forwardedTo: booking.forwardedTo,
  adminNotes: booking.adminNotes,
});

const syncGoogleCalendar = async (
  action: CalendarSyncAction,
  bookingId: string,
  booking: Booking,
  eventId?: string,
  options: { strict?: boolean } = {}
): Promise<string | null> => {
  const { strict = false } = options;
  try {
    const response = await fetch('/api/google-calendar/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action,
        bookingId,
        booking: toCalendarPayload(booking),
        eventId,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Calendar sync request failed.');
    }

    const result = (await response.json()) as { eventId?: string };
    return result.eventId ?? null;
  } catch (error) {
    console.error(`Google Calendar sync failed for ${action}:`, error);
    if (strict) {
      throw error;
    }
    return null;
  }
};

const ensureGoogleCalendarIsReady = async (): Promise<void> => {
  const response = await fetch('/api/google-calendar/status', {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    let reason = 'Google Calendar integration is not configured.';
    try {
      const payload = (await response.json()) as { error?: string };
      if (payload?.error) {
        reason = payload.error;
      }
    } catch {
      // ignore parse errors and use fallback reason
    }

    throw new Error(`Booking unavailable: ${reason}`);
  }
};

export const bookingsService = {
  // Create a new booking
  async create(booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      // Meeting type is fixed across the app.
      const normalizedBooking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'> = {
        ...booking,
        meetingType: 'consultation',
      };

      // Hard-stop booking flow when Calendar integration isn't ready.
      // This prevents any booking write when calendar is not configured.
      await ensureGoogleCalendarIsReady();

      // Check for conflicts
      const conflicts = await this.checkConflict(
        normalizedBooking.scheduledDate,
        normalizedBooking.scheduledTime,
        normalizedBooking.meetingType
      );

      if (conflicts.length > 0) {
        throw new Error('This time slot is already booked. Please select another time.');
      }

      // Remove undefined values - Firestore doesn't accept them
      const bookingData: any = {
        meetingType: normalizedBooking.meetingType,
        attendeeName: normalizedBooking.attendeeName,
        attendeeEmail: normalizedBooking.attendeeEmail,
        scheduledDate: Timestamp.fromDate(normalizedBooking.scheduledDate),
        scheduledTime: normalizedBooking.scheduledTime,
        timezone: normalizedBooking.timezone,
        status: normalizedBooking.status,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Only add optional fields if they have values
      if (normalizedBooking.attendeePhone) {
        bookingData.attendeePhone = normalizedBooking.attendeePhone;
      }
      if (normalizedBooking.companyName) {
        bookingData.companyName = normalizedBooking.companyName;
      }
      if (normalizedBooking.message) {
        bookingData.message = normalizedBooking.message;
      }
      if (normalizedBooking.forwardedTo) {
        bookingData.forwardedTo = normalizedBooking.forwardedTo;
      }
      if (normalizedBooking.adminNotes) {
        bookingData.adminNotes = normalizedBooking.adminNotes;
      }

      const docRef = await addDoc(collection(firestoreDb, BOOKINGS_COLLECTION), bookingData);

      // Sync booking to Google Calendar and store linked event ID.
      // For new bookings, calendar sync is required.
      let calendarEventId: string | null = null;
      try {
        calendarEventId = await syncGoogleCalendar(
          'create',
          docRef.id,
          {
            ...normalizedBooking,
            id: docRef.id,
          },
          undefined,
          { strict: true }
        );

        if (!calendarEventId) {
          throw new Error('Calendar sync did not return an event id.');
        }

        await updateDoc(docRef, {
          googleCalendarEventId: calendarEventId,
          updatedAt: Timestamp.now(),
        });
      } catch (_calendarError) {
        // Prevent "successful booking" without calendar event.
        try {
          await deleteDoc(docRef);
        } catch (cleanupError) {
          console.error('Failed to rollback booking after calendar sync failure:', cleanupError);
        }
        throw new Error('Could not confirm booking with calendar. Please try again.');
      }

      // Send email notifications only after calendar sync succeeds.
      try {
        await emailService.sendBookingEmails(normalizedBooking);
      } catch (emailError) {
        console.error('Failed to send booking emails:', emailError);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  },

  // Check for booking conflicts
  async checkConflict(
    date: Date,
    time: string,
    meetingType: string,
    excludeId?: string
  ): Promise<Booking[]> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      // Get all bookings for the same date
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const q = query(
        collection(firestoreDb, BOOKINGS_COLLECTION),
        where('scheduledDate', '>=', Timestamp.fromDate(startOfDay)),
        where('scheduledDate', '<=', Timestamp.fromDate(endOfDay)),
        where('status', '==', 'confirmed')
      );

      const querySnapshot = await getDocs(q);
      const bookings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Booking[];

      // Filter by time and exclude current booking if editing
      return bookings.filter(
        (booking) =>
          booking.scheduledTime === time &&
          booking.id !== excludeId &&
          booking.status === 'confirmed'
      );
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return [];
    }
  },

  // Get all bookings
  async getAll(): Promise<Booking[]> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const q = query(
        collection(firestoreDb, BOOKINGS_COLLECTION),
        orderBy('scheduledDate', 'desc')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Booking[];
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  },

  // Get bookings for a specific date range
  async getByDateRange(startDate: Date, endDate: Date): Promise<Booking[]> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      // Get all bookings in date range (not just confirmed) - we'll filter by status in the component
      const q = query(
        collection(firestoreDb, BOOKINGS_COLLECTION),
        where('scheduledDate', '>=', Timestamp.fromDate(startDate)),
        where('scheduledDate', '<=', Timestamp.fromDate(endDate)),
        orderBy('scheduledDate', 'asc')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Booking[];
    } catch (error) {
      console.error('Error fetching bookings by date range:', error);
      throw error;
    }
  },

  // Get a single booking by ID
  async getById(id: string): Promise<Booking | null> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const docRef = doc(firestoreDb, BOOKINGS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
        scheduledDate: docSnap.data().scheduledDate?.toDate(),
        createdAt: docSnap.data().createdAt?.toDate(),
        updatedAt: docSnap.data().updatedAt?.toDate(),
      } as Booking;
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  },

  // Update a booking
  async update(id: string, updates: Partial<Booking>): Promise<void> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const bookingRef = doc(firestoreDb, BOOKINGS_COLLECTION, id);

      // Check if document exists before updating
      const docSnap = await getDoc(bookingRef);
      if (!docSnap.exists()) {
        throw new Error(`Booking with ID ${id} does not exist. It may have been deleted.`);
      }

      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      // Only include defined fields (not undefined)
      // Firestore doesn't accept undefined values
      if (updates.meetingType !== undefined) updateData.meetingType = updates.meetingType;
      if (updates.attendeeName !== undefined) updateData.attendeeName = updates.attendeeName;
      if (updates.attendeeEmail !== undefined) updateData.attendeeEmail = updates.attendeeEmail;
      if (updates.attendeePhone !== undefined && updates.attendeePhone !== null) {
        updateData.attendeePhone = updates.attendeePhone;
      } else if (updates.attendeePhone === null) {
        updateData.attendeePhone = deleteField();
      }
      if (updates.companyName !== undefined && updates.companyName !== null) {
        updateData.companyName = updates.companyName;
      } else if (updates.companyName === null) {
        updateData.companyName = deleteField();
      }
      if (updates.message !== undefined && updates.message !== null) {
        updateData.message = updates.message;
      } else if (updates.message === null) {
        updateData.message = deleteField();
      }
      if (updates.scheduledTime !== undefined) updateData.scheduledTime = updates.scheduledTime;
      if (updates.timezone !== undefined) updateData.timezone = updates.timezone;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.forwardedTo !== undefined && updates.forwardedTo !== null) {
        updateData.forwardedTo = updates.forwardedTo;
      } else if (updates.forwardedTo === null) {
        updateData.forwardedTo = deleteField();
      }
      if (updates.adminNotes !== undefined && updates.adminNotes !== null) {
        updateData.adminNotes = updates.adminNotes;
      } else if (updates.adminNotes === null) {
        updateData.adminNotes = deleteField();
      }
      if (updates.googleCalendarEventId !== undefined && updates.googleCalendarEventId !== null) {
        updateData.googleCalendarEventId = updates.googleCalendarEventId;
      } else if (updates.googleCalendarEventId === null) {
        updateData.googleCalendarEventId = deleteField();
      }

      if (updates.scheduledDate) {
        updateData.scheduledDate = Timestamp.fromDate(updates.scheduledDate);
      }

      await updateDoc(bookingRef, updateData);
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  },

  // Cancel a booking
  async cancel(id: string): Promise<void> {
    const existingBooking = await this.getById(id);
    if (!existingBooking) {
      throw new Error(`Booking with ID ${id} does not exist. It may have been deleted.`);
    }

    await this.update(id, { status: 'cancelled' });

    if (existingBooking.googleCalendarEventId) {
      await syncGoogleCalendar(
        'cancel',
        id,
        existingBooking,
        existingBooking.googleCalendarEventId
      );
    }

    try {
      await emailService.sendBookingActionEmail({
        booking: existingBooking,
        action: 'cancelled',
      });
    } catch (emailError) {
      console.error('Failed to send cancellation email:', emailError);
    }
  },

  // Approve a booking (change from pending to confirmed)
  async approve(id: string): Promise<void> {
    await this.update(id, { status: 'confirmed' });
  },

  // Mark booking as completed
  async complete(id: string): Promise<void> {
    await this.update(id, { status: 'completed' });
  },

  // Forward/reschedule a booking
  async forward(
    id: string,
    forwardedTo: string,
    newDate?: Date,
    newTime?: string,
    adminNotes?: string
  ): Promise<void> {
    const existingBooking = await this.getById(id);
    if (!existingBooking) {
      throw new Error(`Booking with ID ${id} does not exist. It may have been deleted.`);
    }

    const updateData: any = {
      forwardedTo,
      adminNotes,
    };

    if (newDate && newTime) {
      updateData.scheduledDate = newDate;
      updateData.scheduledTime = newTime;
    }

    await this.update(id, updateData);

    const updatedBooking: Booking = {
      ...existingBooking,
      forwardedTo,
      adminNotes,
      scheduledDate: newDate || existingBooking.scheduledDate,
      scheduledTime: newTime || existingBooking.scheduledTime,
      updatedAt: new Date(),
    };

    const syncedEventId = await syncGoogleCalendar(
      'update',
      id,
      updatedBooking,
      existingBooking.googleCalendarEventId
    );

    if (syncedEventId && syncedEventId !== existingBooking.googleCalendarEventId) {
      await this.update(id, { googleCalendarEventId: syncedEventId });
    }

    try {
      await emailService.sendBookingActionEmail({
        booking: updatedBooking,
        action: newDate && newTime ? 'rescheduled' : 'forwarded',
        previousDate: existingBooking.scheduledDate,
        previousTime: existingBooking.scheduledTime,
        forwardedTo,
        adminNotes,
      });
    } catch (emailError) {
      console.error('Failed to send forward/reschedule email:', emailError);
    }
  },

  // Delete a booking
  async delete(id: string): Promise<void> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const existingBooking = await this.getById(id);
      if (existingBooking?.googleCalendarEventId) {
        await syncGoogleCalendar(
          'cancel',
          id,
          existingBooking,
          existingBooking.googleCalendarEventId
        );
      }

      const bookingRef = doc(firestoreDb, BOOKINGS_COLLECTION, id);
      await deleteDoc(bookingRef);
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  },

  // Get booking settings
  async getSettings(): Promise<BookingSettings | null> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const q = query(collection(firestoreDb, SETTINGS_COLLECTION), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Return default settings
        return {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          slotDuration: 45,
          bufferTime: 15,
          advanceBookingDays: 30,
          workingDays: [1, 2, 3, 4, 5], // Mon-Fri
          availability: [],
          blockedDates: [],
        };
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as BookingSettings;
    } catch (error) {
      console.error('Error fetching booking settings:', error);
      return null;
    }
  },

  // Update booking settings
  async updateSettings(settings: Partial<BookingSettings>): Promise<void> {
    try {
      if (!firestoreDb) {
        throw new Error('Firestore database is not initialized.');
      }

      const currentSettings = await this.getSettings();

      if (currentSettings?.id) {
        const settingsRef = doc(firestoreDb, SETTINGS_COLLECTION, currentSettings.id);
        await updateDoc(settingsRef, {
          ...settings,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Create new settings
        await addDoc(collection(firestoreDb, SETTINGS_COLLECTION), {
          ...settings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating booking settings:', error);
      throw error;
    }
  },
};
